import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { aiAdminAPI, getRecentSearches, profileAPI } from '../services/api';
import AIInsightOutput from './AIInsightOutput';
import {
  AIAuditEntry,
  AICacheStats,
  AICredits,
  AIModelOption,
  AIPromptSummary,
  AIPromptTestResult,
  AIPromptVersion,
  UserWithStats,
} from '../types';

interface AISettingsPanelProps {
  isSuperAdmin: boolean;
  currentSearchProfile?: Record<string, unknown>;
}

interface RecentSearchOption {
  id: number;
  name: string;
  address?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

type Msg = { type: 'success' | 'error'; text: string } | null;

const fmtCredits = (v: number | null | undefined) => (v == null ? 'n/a' : `$${v.toFixed(2)}`);
const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : 'n/a');

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#283E56', mb: 1.5 }}>{title}</Typography>
    {children}
  </Paper>
);

const Stat: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Typography variant={strong ? 'h5' : 'body1'} sx={{ fontWeight: strong ? 700 : 500 }}>{value}</Typography>
  </Box>
);

const AISettingsPanel: React.FC<AISettingsPanelProps> = ({ isSuperAdmin, currentSearchProfile }) => {
  const [prompts, setPrompts] = useState<AIPromptSummary[]>([]);
  const [category, setCategory] = useState('');
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [versions, setVersions] = useState<AIPromptVersion[]>([]);
  const [viewVersionId, setViewVersionId] = useState<number | null>(null);
  const [models, setModels] = useState<AIModelOption[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [credits, setCredits] = useState<AICredits | null>(null);
  const [audit, setAudit] = useState<AIAuditEntry[]>([]);
  const [superAdmins, setSuperAdmins] = useState<UserWithStats[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearchOption[]>([]);
  const [selectedTestProfileId, setSelectedTestProfileId] = useState('');
  const [testResult, setTestResult] = useState<AIPromptTestResult | null>(null);
  const [busy, setBusy] = useState<string | null>('init');
  const [msg, setMsg] = useState<Msg>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (msg) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [msg]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearText, setClearText] = useState('');
  const [tab, setTab] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState<string | null>(null);
  const [cacheStats, setCacheStats] = useState<AICacheStats | null>(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [resultOpen, setResultOpen] = useState(true);

  useEffect(() => {
    if (testResult) setResultOpen(true);
  }, [testResult]);

  const loadCredits = async () => {
    setCreditsLoading(true);
    setCreditsError(null);
    try {
      setCredits(await aiAdminAPI.getCredits());
    } catch (e) {
      setCreditsError(e instanceof Error ? e.message : 'Failed to load credits');
    } finally {
      setCreditsLoading(false);
    }
  };

  const loadCache = async () => {
    setCacheLoading(true);
    setCacheError(null);
    try {
      setCacheStats(await aiAdminAPI.getCacheStats());
    } catch (e) {
      setCacheError(e instanceof Error ? e.message : 'Failed to load cache stats');
    } finally {
      setCacheLoading(false);
    }
  };

  // Load balance and cache stats when their tabs open
  useEffect(() => {
    if (tab === 1 && !credits && !creditsLoading) loadCredits();
    if (tab === 2 && !cacheStats && !cacheLoading) loadCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const current = prompts.find((p) => p.category === category);
  const viewedVersion = versions.find((v) => v.id === viewVersionId) || versions.find((v) => v.is_active);
  const baseText = viewedVersion?.prompt_text ?? current?.prompt_text ?? '';
  const isDirty = current ? draft !== baseText : false;
  const viewingInactive = !!viewedVersion && !viewedVersion.is_active;
  const activeModel = models.find((m) => m.is_active);
  const selectedRecentSearch = recentSearches.find((search) => String(search.id) === selectedTestProfileId);
  const selectedTestProfile = selectedTestProfileId === 'current' && currentSearchProfile
    ? currentSearchProfile
    : selectedRecentSearch
      ? {
          first_name: selectedRecentSearch.first_name || '',
          last_name: selectedRecentSearch.last_name || '',
          city: selectedRecentSearch.city || '',
          state: selectedRecentSearch.state || '',
          street: selectedRecentSearch.street || '',
          zip: selectedRecentSearch.zip_code || '',
        }
      : undefined;
  const selectedTestInputs = selectedTestProfileId === 'current' && currentSearchProfile
    ? currentSearchProfile
    : selectedRecentSearch;

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setMsg({ type: 'error', text: e instanceof Error ? e.message : 'Request failed' });
    } finally {
      setBusy(null);
    }
  };

  // Load a category's versions and show the active one (or a specific one) in the editor
  const loadVersions = async (cat: string, selectId?: number) => {
    let list: AIPromptVersion[] = [];
    try {
      list = await aiAdminAPI.listPromptVersions(cat);
    } catch {
      list = [];
    }
    setVersions(list);
    const target = list.find((v) => v.id === selectId) || list.find((v) => v.is_active);
    setViewVersionId(target ? target.id : null);
    if (target) setDraft(target.prompt_text);
  };

  const refreshPromptsAndAudit = async (cat: string) => {
    const data = await aiAdminAPI.listPrompts();
    setPrompts(data);
    const p = data.find((x) => x.category === cat);
    if (p) setDraft(p.prompt_text);
    await loadVersions(cat);
    setAudit(await aiAdminAPI.getAuditLog());
  };

  const refreshModels = async () => {
    const m = await aiAdminAPI.listModels();
    setModels(m.models);
  };

  useEffect(() => {
    getRecentSearches().then((r) => {
      setRecentSearches(r.searches || []);
    }).catch(() => setRecentSearches([]));
  }, []);

  useEffect(() => {
    if (currentSearchProfile) {
      setSelectedTestProfileId('current');
    } else {
      setSelectedTestProfileId((selectedId) =>
        selectedId && selectedId !== 'current' ? selectedId : String(recentSearches[0]?.id || ''),
      );
    }
  }, [currentSearchProfile, recentSearches]);

  // Initial load
  useEffect(() => {
    run('init', async () => {
      const data = await aiAdminAPI.listPrompts();
      setPrompts(data);
      if (data.length) setCategory(data[0].category);
      const m = await aiAdminAPI.listModels();
      setModels(m.models);
      setSelectedModel(m.active_model || '');
      setAudit(await aiAdminAPI.getAuditLog());
      if (isSuperAdmin) {
        const users = await profileAPI.listUsers();
        setSuperAdmins(users.filter((u) => u.role === 'superadmin'));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Category change: load draft and version history
  useEffect(() => {
    if (!category) return;
    const p = prompts.find((x) => x.category === category);
    if (p) setDraft(p.prompt_text);
    setNote('');
    setTestResult(null);
    setVersions([]);
    setViewVersionId(null);
    loadVersions(category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handlePublish = () =>
    run('publish', async () => {
      await aiAdminAPI.publishPrompt(category, { prompt_text: draft, note: note || null });
      await refreshPromptsAndAudit(category);
      setNote('');
      setPublishOpen(false);
      setMsg({ type: 'success', text: 'Prompt published for all users. Cache was not cleared.' });
    });

  const handleSelectVersion = (v: AIPromptVersion) => {
    if (v.id === viewedVersion?.id) return;
    if (isDirty && !window.confirm('You have unpublished edits. Discard them and view this version?')) return;
    setViewVersionId(v.id);
    setDraft(v.prompt_text);
  };

  const handleRollback = (v: AIPromptVersion) =>
    run(`rb-${v.id}`, async () => {
      await aiAdminAPI.rollbackPrompt(category, v.id);
      await refreshPromptsAndAudit(category);
      setMsg({ type: 'success', text: `${category}: v${v.version_no} is now active` });
    });

  const handleDeleteVersion = (v: AIPromptVersion) => {
    if (!window.confirm(`Delete ${category} version v${v.version_no}? This cannot be undone.`)) return;
    run(`del-${v.id}`, async () => {
      await aiAdminAPI.deletePromptVersion(category, v.id);
      await refreshPromptsAndAudit(category);
      setMsg({ type: 'success', text: `${category}: deleted v${v.version_no}` });
    });
  };

  const handleTest = () =>
    run('test', async () => {
      if (!selectedTestProfile || !selectedTestInputs) {
        throw new Error('Select a profile before running the test');
      }
      const r = await aiAdminAPI.testPrompt(category, {
        prompt_text: draft,
        model_id: selectedModel,
        first_name: String(selectedTestInputs.first_name || ''),
        last_name: String(selectedTestInputs.last_name || ''),
        city: String(selectedTestInputs.city || ''),
        state: String(selectedTestInputs.state || ''),
        profile_data: selectedTestProfile,
      });
      setTestResult(r);
    });

  const handleSaveModel = () =>
    run('model', async () => {
      await aiAdminAPI.setActiveModel(selectedModel);
      await refreshModels();
      setAudit(await aiAdminAPI.getAuditLog());
      setMsg({ type: 'success', text: 'Active model updated for all users' });
    });

  const handleClearCache = () =>
    run('clear', async () => {
      const r = await aiAdminAPI.clearCache(clearText);
      setClearOpen(false);
      setClearText('');
      loadCache();
      setAudit(await aiAdminAPI.getAuditLog());
      setMsg({ type: 'success', text: `Cleared ${r.deleted_rows} cached AI reports` });
    });

  const handleToggleGrant = (u: UserWithStats) =>
    run(`grant-${u.id}`, async () => {
      await aiAdminAPI.setUserAiAdmin(u.id, !u.is_ai_admin);
      setSuperAdmins((list) => list.map((x) => (x.id === u.id ? { ...x, is_ai_admin: !x.is_ai_admin } : x)));
    });

  if (busy === 'init') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box ref={topRef}>
      {msg && (
        <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Box sx={{ position: 'sticky', top: -20, zIndex: 2, bgcolor: 'background.paper', mx: -3, px: 3, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Prompt${isDirty ? ' *' : ''}`} />
          <Tab label="Credits" />
          <Tab label="Cache" />
          <Tab label="Activity and access" />
        </Tabs>
      </Box>

      {tab === 0 && (<>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' }, gap: 2, alignItems: 'start' }}>
      <Section title="Prompt">
        <Autocomplete
          size="small"
          fullWidth
          disableClearable
          sx={{ mb: 2 }}
          options={prompts.map((p) => p.category)}
          value={category}
          onChange={(_, val) => val && setCategory(val)}
          renderInput={(params) => <TextField {...params} label="Category" placeholder="Search categories" />}
        />

        {versions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Versions – click one to view its exact prompt
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {versions.map((v) => (
                <Chip
                  key={v.id}
                  clickable
                  size="small"
                  label={`v${v.version_no}${v.is_active ? ' • Active' : ''}`}
                  color={v.is_active ? 'success' : 'default'}
                  variant={v.id === viewedVersion?.id ? 'filled' : 'outlined'}
                  onClick={() => handleSelectVersion(v)}
                  sx={v.id === viewedVersion?.id ? { boxShadow: '0 0 0 2px #283E56' } : undefined}
                />
              ))}
            </Stack>
            {viewedVersion && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                v{viewedVersion.version_no} – {fmtDate(viewedVersion.created_at)}
                {viewedVersion.note ? ` – ${viewedVersion.note}` : ''}
              </Typography>
            )}
          </Box>
        )}

        {viewingInactive && viewedVersion && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={0.5}>
                <Button size="small" disabled={!!busy || isDirty} onClick={() => handleRollback(viewedVersion)}>
                  Make active
                </Button>
                <Button size="small" color="error" disabled={!!busy} onClick={() => handleDeleteVersion(viewedVersion)}>
                  Delete
                </Button>
              </Stack>
            }
          >
            Viewing v{viewedVersion.version_no}, which is not live. Publishing edits creates a new version.
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          minRows={10}
          maxRows={24}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          helperText="Edits are only sent to users after Publish."
        />

        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} alignItems="center">
          <TextField
            size="small"
            label="Change note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" disabled={!isDirty || !!busy} onClick={() => setDraft(baseText)}>
            Discard
          </Button>
          <Button variant="contained" disabled={!isDirty || !!busy} onClick={() => setPublishOpen(true)}>
            Publish
          </Button>
        </Stack>
      </Section>
      <Box sx={{ minWidth: 0 }}>
      <Section title="Model">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Model</InputLabel>
            <Select label="Model" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value as string)}>
              {models.map((m) => (
                <MenuItem key={m.model_id} value={m.model_id}>
                  {`${m.label}${m.is_active ? ' (active)' : ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            disabled={!selectedModel || selectedModel === activeModel?.model_id || !!busy}
            onClick={handleSaveModel}
          >
            Save as active model
          </Button>
        </Stack>

      </Section>

      <Section title="Test run (not saved, cache not used)">
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel id="test-profile-select-label">Profile</InputLabel>
          <Select
            labelId="test-profile-select-label"
            label="Profile"
            value={selectedTestProfileId}
            onChange={(event) => {
              setSelectedTestProfileId(event.target.value);
              setTestResult(null);
            }}
          >
            {currentSearchProfile && (
              <MenuItem value="current">
                Current search: {[currentSearchProfile.first_name, currentSearchProfile.last_name].filter(Boolean).join(' ')}
              </MenuItem>
            )}
            {recentSearches.map((search) => (
              <MenuItem key={search.id} value={String(search.id)}>
                {search.name}{search.address ? `, ${search.address}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedTestProfile && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {[selectedTestInputs?.first_name, selectedTestInputs?.last_name].filter(Boolean).join(' ')}
            </Typography>
            <Typography variant="body2">
              {[selectedTestInputs?.street, selectedTestInputs?.city, selectedTestInputs?.state, selectedTestInputs?.zip_code]
                .filter(Boolean)
                .join(', ') || 'No address on file'}
            </Typography>
          </Alert>
        )}
        {!selectedTestProfile && (
          <Alert severity="info" sx={{ mb: 1.5 }}>Choose a profile from the list to run a test.</Alert>
        )}
        <Button variant="outlined" disabled={!draft.trim() || !selectedModel || !selectedTestProfile || !!busy} onClick={handleTest}>
          {busy === 'test' ? 'Running...' : 'Run test'}
        </Button>
        {testResult && (
          <Button sx={{ mt: 1.5 }} variant="text" onClick={() => setResultOpen(true)}>View last result</Button>
        )}
        <Dialog open={!!testResult && resultOpen} onClose={() => setResultOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Test result</DialogTitle>
          <DialogContent dividers>
            {testResult?.output ? (
              <AIInsightOutput output={testResult.output} />
            ) : (
              <Typography color="text.secondary">No output</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResultOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Section>
      </Box>
      </Box>

      </>)}
      {tab === 1 && (<>
      <Section title="Account balance">
        {creditsLoading && <CircularProgress size={22} />}
        {creditsError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadCredits}>Retry</Button>}>
            Credits unavailable: {creditsError}
          </Alert>
        )}
        {credits && !creditsError && !credits.management_key_configured && (
          <Alert severity="info">Set OPENROUTER_MANAGEMENT_KEY in the backend environment to show the remaining balance.</Alert>
        )}
        {credits && !creditsError && credits.management_key_configured && credits.account_error && (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={loadCredits}>Retry</Button>}>
            Balance unavailable: {credits.account_error}
          </Alert>
        )}
        {credits && !creditsError && credits.management_key_configured && !credits.account_error && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box sx={{ p: 2.5, borderRadius: 2, background: 'linear-gradient(135deg, #283E56 0%, #3E6A8A 100%)', color: '#fff' }}>
              <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1 }}>Remaining</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{fmtCredits(credits.account_balance)}</Typography>
            </Box>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Total purchased</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: '#283E56' }}>{fmtCredits(credits.account_total_credits)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Total spent</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: '#283E56' }}>{fmtCredits(credits.account_total_usage)}</Typography>
            </Paper>
          </Box>
        )}
        <Button variant="outlined" size="small" href="https://openrouter.ai/settings/credits" target="_blank" rel="noopener noreferrer" sx={{ mt: 2 }}>
          Buy credits on OpenRouter
        </Button>
      </Section>

      </>)}
      {tab === 2 && (<>
      <Section title="Insights cache">
        {cacheLoading && <CircularProgress size={22} />}
        {cacheError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadCache}>Retry</Button>}>
            Cache stats unavailable: {cacheError}
          </Alert>
        )}
        {cacheStats && (<>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Stat label="Cached reports" value={String(cacheStats.total)} strong />
            <Stat label="Succeeded" value={String(cacheStats.succeeded)} />
            <Stat label="Failed" value={String(cacheStats.failed)} />
            <Stat label="Expired" value={String(cacheStats.expired)} />
          </Stack>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Oldest: {fmtDate(cacheStats.oldest)} | Newest: {fmtDate(cacheStats.newest)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Last cleared: {cacheStats.last_cleared_at ? `${fmtDate(cacheStats.last_cleared_at)} by ${cacheStats.last_cleared_by || 'unknown'}` : 'Never'}
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>By category</Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Reports</TableCell>
                <TableCell>Newest</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cacheStats.by_category.map((c) => (
                <TableRow key={c.category}>
                  <TableCell>{c.category}</TableCell>
                  <TableCell align="right">{c.total}</TableCell>
                  <TableCell>{fmtDate(c.newest)}</TableCell>
                </TableRow>
              ))}
              {cacheStats.by_category.length === 0 && (
                <TableRow><TableCell colSpan={3}>No cached reports</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>By model</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Model</TableCell>
                <TableCell align="right">Reports</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cacheStats.by_model.map((m) => (
                <TableRow key={m.model_used}>
                  <TableCell>{m.model_used}</TableCell>
                  <TableCell align="right">{m.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>)}

        <Divider sx={{ my: 2 }} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          Clearing deletes every saved AI report for all users. Reports are regenerated on next view using OpenRouter credits.
          Publishing a prompt does not clear the cache, so existing reports keep the older prompt until they are cleared or expire.
        </Alert>
        <Button color="error" variant="outlined" disabled={!!busy} onClick={() => setClearOpen(true)}>
          Clear insights cache
        </Button>
      </Section>

      </>)}
      {tab === 3 && (<>
      {isSuperAdmin && (
        <Section title="AI admin access">
          {superAdmins.length === 0 && <Typography variant="body2">No superadmin accounts.</Typography>}
          {superAdmins.map((u) => (
            <FormControlLabel
              key={u.id}
              control={
                <Switch
                  checked={!!u.is_ai_admin}
                  disabled={!!busy}
                  onChange={() => handleToggleGrant(u)}
                />
              }
              label={`${u.first_name} ${u.last_name} (${u.email})`}
              sx={{ display: 'flex' }}
            />
          ))}
        </Section>
      )}

      <Section title="Recent AI admin activity">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Category</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit.slice(0, 20).map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</TableCell>
                <TableCell>{a.user_email || ''}</TableCell>
                <TableCell>{a.action}</TableCell>
                <TableCell>{a.category || ''}</TableCell>
              </TableRow>
            ))}
            {audit.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No activity yet</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Section>
      </>)}

      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish prompt?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This changes the {category} prompt for all users immediately. Existing cached reports are kept.
            Clear the cache separately if you want them regenerated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!!busy} onClick={handlePublish}>Publish</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearOpen} onClose={() => setClearOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="error" /> Clear insights cache?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This deletes every cached AI report for all users. Reports will be regenerated on next view, which uses OpenRouter credits.
            Type CLEAR to confirm.
          </DialogContentText>
          <TextField fullWidth size="small" value={clearText} onChange={(e) => setClearText(e.target.value)} placeholder="CLEAR" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setClearOpen(false); setClearText(''); }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={clearText !== 'CLEAR' || !!busy} onClick={handleClearCache}>
            Clear cache
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISettingsPanel;
